/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/CustomerCompanyUsers/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SvgIcon } from "@mui/material";
import { ReactComponent as AddIcon } from "../../assets/icons/CustomerCompanyUsers/AddIcon.svg";
import styles from "./CustomerCompanyUsers.module.css";
import CustomerCompanyUsersTable from "../CustomerCompanyUsersTable";
import CustomerCompanyUserModal from "../../modals/CustomerCompanyUserModal";

const CustomerCompanyUsers = ({ companyId, isNewCompany, navigate }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedUser, setSelectedUser] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleOpenCreateModal = () => {
    setModalMode("create");
    setSelectedUser(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (user) => {
    setModalMode("edit");
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const handleUserCreated = () => {
    // Trigger a refresh of the users list
    setRefreshTrigger((prev) => prev + 1);
  };

  return (
    <div className={styles.container}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>
          {t("components.customerCompanyUsers.header")}
        </span>
        <div className={styles.sectionDivider}></div>
      </div>

      <div className={styles.header}>
        <div
          className={`${styles.createButton} ${
            isNewCompany ? styles.disabled : ""
          }`}
          onClick={!isNewCompany ? handleOpenCreateModal : undefined}
        >
          <SvgIcon
            component={AddIcon}
            fontSize="small"
            className={styles.createButtonIcon}
          />
          {t("components.customerCompanyUsers.createButton")}
        </div>
      </div>

      <CustomerCompanyUsersTable
        companyId={companyId}
        isNewCompany={isNewCompany}
        onEditUser={handleOpenEditModal}
        navigate={navigate}
        refreshTrigger={refreshTrigger}
      />

      <CustomerCompanyUserModal
        open={isModalOpen}
        onClose={handleCloseModal}
        companyId={companyId}
        user={selectedUser}
        mode={modalMode}
        onUserCreated={handleUserCreated}
      />
    </div>
  );
};

export default CustomerCompanyUsers;
